import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router";
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  message,
  Modal,
  Typography,
} from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { API_BASE_URL } from "../../config/api.config";
import { getFileExtension } from "../../utils/getFileExtension";
import pallete from "../../utils/pallete";
import { waterTankService } from "../../services/water-tank.service";
import { format } from "date-fns";

export default function WaterTankDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const breakpoint = Grid.useBreakpoint();
  const [previewImage, setPreviewImage] = useState("");
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [messageApi, messageHolder] = message.useMessage();

  const { data: apiData, isLoading } = useQuery({
    queryKey: ["waterTankDetail", id],
    queryFn: () => waterTankService.getById(id),
  });

  const surveyData = apiData?.data;

  if (isLoading) return <p>Loading...</p>;

  const handleVerify = async () => {
    try {
      setLoadingVerify(true);
      await waterTankService.verify(id, { actor: "currentUser" });
      messageApi.success("Verifikasi berhasil!");
      setTimeout(() => navigate("/water-tank"), 500);
    } catch (error) {
      messageApi.error(
        error.response?.data?.message || "Verifikasi gagal. Silakan coba lagi."
      );
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleRejectConfirm = async () => {
    try {
      setRejectModalOpen(false);
      await waterTankService.reject(id, { actor: "currentUser" });
      messageApi.success("Ajuan berhasil ditolak.");
      setTimeout(() => navigate("/water-tank"), 500);
    } catch (error) {
      messageApi.error(
        error.response?.data?.message || "Cancel gagal. Silakan coba lagi."
      );
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(surveyData.image, {
        mode: "cors",
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = surveyData.image.split("/").pop(); // nama file dari URL
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download gagal:", error);
    }
  };

  return (
    <>
      <Card title="Detail Air Tangki">
        <Descriptions
          style={{ marginBottom: 20 }}
          column={2}
          bordered
          size="small"
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={[
            {
              label: "Nama Pelanggan",
              children: surveyData?.Nomen?.name || "-",
            },
            {
              label: "Nomen/Norek",
              children: surveyData?.Nomen?.nomen || "-",
            },
            {
              label: "No Telepon",
              children: surveyData?.Customer?.phone || "-",
            },
            {
              label: "Email",
              children: surveyData?.Customer?.email || "-",
            },
            {
              label: "Alamat",
              children: surveyData?.Nomen?.address || "-",
              span: 2,
            },
            {
              label: "Regional",
              children: surveyData?.Nomen?.area || "-",
            },
            {
              label: "Tanggal Permintaan",
              children: surveyData?.requestDate
                ? format(new Date(surveyData.requestDate), "dd/MM/yyyy - HH.mm")
                : "-",
            },
            {
              label: "Alasan Permintaan",
              span: 2,
              children: surveyData?.reason,
            },
            {
              label: "Foto Pendukung",
              span: 2,
              children: surveyData?.image && (
                <Flex gap={8}>
                  <Typography.Text style={{ marginRight: "auto" }}>
                    {surveyData.image.split("/").pop()}
                  </Typography.Text>
                  <Flex vertical={!breakpoint.lg} gap={8}>
                    <Button
                      icon={
                        <Icon
                          icon="solar:download-square-bold-duotone"
                          width="18"
                          height="18"
                          style={{ marginBottom: -1 }}
                        />
                      }
                      variant="filled"
                      color="primary"
                      size="small"
                      style={{ fontSize: 12 }}
                      onClick={handleDownload}
                    >
                      Unduh
                    </Button>

                    <Button
                      variant="filled"
                      color="primary"
                      size="small"
                      style={{ fontSize: 12 }}
                      icon={
                        <Icon
                          icon="solar:eye-bold-duotone"
                          width="18"
                          height="18"
                          style={{ marginBottom: -1 }}
                        />
                      }
                      onClick={() => setPreviewImage(surveyData.image)}
                    >
                      Lihat
                    </Button>
                  </Flex>
                </Flex>
              ),
            },
          ]}
        />

        {surveyData.status !== "verified" &&
        surveyData.status !== "rejected" ? (
          <Flex justify="flex-end" gap={12} style={{ marginTop: 24 }}>
            <Button
              danger
              style={{
                backgroundColor: pallete.danger.main,
                color: "#fff",
                fontSize: 14,
              }}
              onClick={() => setRejectModalOpen(true)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              style={{
                backgroundColor: pallete.curiousBlue[500],
                color: "#fff",
                fontSize: 14,
              }}
              onClick={handleVerify}
              loading={loadingVerify}
            >
              Verifikasi
            </Button>
          </Flex>
        ) : null}
      </Card>

      <Modal
        open={previewImage.length > 0}
        centered
        title="Gambar"
        onCancel={() => setPreviewImage("")}
        footer={[
          <Button
            key="cancel"
            variant="outlined"
            color="primary"
            onClick={() => setPreviewImage("")}
          >
            Tutup
          </Button>,
        ]}
      >
        <embed
          style={{
            width: "100%",
            height: getFileExtension(previewImage) === "pdf" ? "500px" : "auto",
          }}
          src={previewImage}
        />
      </Modal>

      <Modal
        open={rejectModalOpen}
        centered
        footer={null}
        onCancel={() => setRejectModalOpen(false)}
      >
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ textAlign: "center", padding: "20px 0" }}
        >
          <Icon
            icon="solar:trash-bin-2-bold-duotone"
            width="128"
            height="128"
            style={{ color: pallete.danger.main, marginBottom: 16 }}
          />
          <Typography.Title level={4}>Konfirmasi</Typography.Title>
          <Typography.Text>
            Apakah Anda yakin akan tolak pelaporan?
          </Typography.Text>
          <Flex gap={12} style={{ marginTop: 24 }}>
            <Button onClick={() => setRejectModalOpen(false)}>Batal</Button>
            <Button
              danger
              type="primary"
              onClick={handleRejectConfirm}
              style={{ backgroundColor: pallete.danger.main }}
            >
              Ya, Tolak
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
